
import {
    ErrorInfo,
    JoinOptions,
    JoinChannelResponse,
    LeaveChannelResponse,
    JoinTopicResponse,
    PublishTopicMessageResponse,
    LeaveTopicResponse,
    SubscribeTopicResponse,
    UnsubscribeTopicResponse
} from "./rtmTypes";
export default class RTMStreamChannel {
    private _channelName: string;
    constructor(channelName: string) {
        // constructor logic here
        this._channelName = channelName;
    }

    //调用 join 方法加入 Stream Channel。加入频道后，你才能进行频道相关操作。此时，订阅该频道并添加事件监听的用户会收到如下事件通知
    //SNAPSHOT 类型的 presence 事件通知。
    //SNAPSHOT 类型的 topic 事件通知。
    async join(options?: JoinOptions): Promise<JoinChannelResponse|ErrorInfo>{
        try {
            //加入频道
            const response: JoinChannelResponse = {
                channelName: this._channelName, // replace with actual channel name
                timeToken: Date.now() // replace with actual timestamp
            };
            return response;
        } catch (error: any) {
            const errorInfo: ErrorInfo = {
                error: true,
                operation: "join",
                errorCode: error.code,
                reason: error.message
            };
            return errorInfo;
        }
    }

    //如果你不再需要待在某个频道中，你可以调用 leave 方法离开该频道。离开后你将不再会接收到此频道中的任何消息、
    //状态及事件通知，你在该频道中创建的 Topic 发布者的角色及你与所有 Topic 的订阅关系都将自动解除。如果想再次
    //恢复之前的发布者角色和订阅关系，你需要调用 join 方法再次进入该频道并自行调用 joinTopic 和 subscribeTopic 
    //方法进行设置。成功离开频道后，频道中的远端用户会收到 REMOTE_LEAVE 类型的 presence 事件通知
    //如果方法调用成功，则 SDK 返回一个 LeaveChannelResponse
    //如果方法调用失败，则返回一个 ErrorInfo 类型数据
    async leave(): Promise<LeaveChannelResponse|ErrorInfo>{
        try {
            //离开频道
            const response: LeaveChannelResponse = {
                channelName: this._channelName, // replace with actual channel name
                timeToken: Date.now() // replace with actual timestamp
            };
            return response;
        } catch (error: any) {
            const errorInfo: ErrorInfo = {
                error: true,
                operation: "leave",
                errorCode: error.code,
                reason: error.message
            };
            return errorInfo;
        }
    }

    //加入 Topic 的目的在于注册成为该 Topic 的消息发布者之一，从而让用户具备发送消息的能力。是否
    //执行该操作都不影响该用户是否成为该 Topic 的订阅者。当前 RTM 支持单客户端在同一个 Stream Channel 中
    //同时加入 8 个 Topic。在执行此操作之前，用户需要首先创建 RTMStreamChannel 对象实例，并调用 join 方法
    //加入频道,成功加入 Topic 后，订阅该 Topic 并添加事件监听的用户会收到 REMOTE_JOIN 类型的 topic 事件通知
    async joinTopic(topicName: string, options?:{meta?:any}): Promise<JoinTopicResponse|ErrorInfo>{
        try {
            //加入 Topic
            const response: JoinTopicResponse = {

                topicName: topicName, // replace with actual topic name
                timeToken: Date.now() // replace with actual timestamp
            };
            return response;
        } catch (error: any) {
            const errorInfo: ErrorInfo = {
                error: true,
                operation: "joinTopic",
                errorCode: error.code,
                reason: error.message
            };
            return errorInfo;
        }
    }


    //publishTopicMessage 方法用于向 Topic 发送消息，频道中订阅此 Topic 及此消息发布者的用户会在100 毫秒内收到此消息。
    //调用 publishTopicMessage 方法需要用户首先加入 Stream Channel 并且通过调用 joinTopic 方法注册成为该 Topic 的消息
    //发布者。用户发送的消息在传输的过程中会被 TLS 加密，数据链路加密默认开启且无法关闭。用户也可以在出初始化的时候开启端
    //侧加密，以获得更高的数据安全等级
    async publishTopicMessage(topicName: string, message: string, options?:{customType?: string;}): Promise<PublishTopicMessageResponse|ErrorInfo>{
        try {
            // 发送消息
            const response: PublishTopicMessageResponse = {
                topicName: topicName, // replace with actual topic name
                timeToken: Date.now() // replace with actual timestamp
            };
            return response;
        } catch (error: any) {
            const errorInfo: ErrorInfo = {
                error: true,
                operation: "publishTopicMessage",
                errorCode: error.code,
                reason: error.message
            };
            return errorInfo;
        }
    }

    //当不再需要向某个 Topic 发布消息，你可以调用 leaveTopic 方法取消注册为该 Topic 的消息发布者，从而释放资源。该方法不影响
    //是否订阅该 Topic，也不影响其他用户对该 Topic 的任何操作。成功调用该方法后，订阅该频道并开启事件监听的用户会收到 REMOTE_LEAVE 类型的 topic 事件通知
    async leaveTopic(topicName: string): Promise<LeaveTopicResponse|ErrorInfo>{
        try {
            // 离开 Topic
            const response: LeaveTopicResponse = {
                topicName: topicName, // replace with actual topic name
                timeToken: Date.now() // replace with actual timestamp
            };
            return response;
        } catch (error: any) {
            const errorInfo: ErrorInfo = {
                error: true,
                operation: "leaveTopic",
                errorCode: error.code,
                reason: error.message
            };
            return errorInfo;
        }
    }

    //加入频道后，你可以调用 subscribeTopic 方法订阅该频道中 Topic 的消息发布者。subscribeTopic 为增量方法。
    //例如，第一次调用该方法时，订阅消息发布者列表为 [UserA, UserB]，第二次调用该方法时，订阅消息发布者列表为 [UserB, UserC]，
    //则最后成功订阅的结果是 [UserA, UserB, UserC]。同一个用户在同一个频道中最多只能同时订阅 50 个 Topic，且每个 Topic 中最多
    //只能订阅 64 个消息发布者
    async subscribeTopic(topicName: string, options?: {users?: string[];}): Promise<SubscribeTopicResponse|ErrorInfo>{
        try {
            // 订阅 Topic
            const response: SubscribeTopicResponse = {
                succeedUsers: [],       // 订阅成功的用户列表
                failedUsers: [],        // 订阅失败的用户列表
                failedDetails: [              // 订阅失败用户原因
                    {
                        user: 'pcg',         // 用户ID
                        errorCode: 0,    // 错误码
                        reason: "success"        // 错误原因
                    },
                ],
                timeToken: Date.now(),            // （预留属性）操作成功时间戳
                topiclName: "TopicName"            // 订阅 Topic 的名称
            };
            return response;
        } catch (error: any) {
            const errorInfo: ErrorInfo = {
                error: true,
                operation: "subscribeTopic",
                errorCode: error.code,
                reason: error.message
            };
            return errorInfo;
        }
    }

    //如果你对某个 Topic 不再感兴趣，或者不再需要订阅 Topic 中的一个或多个消息发布者，你可以
    //调用 unsubscribeTopic 方法取消订阅该 Topic 或取消订阅该 Topic 中指定的消息发布者
    //users: 需要取消订阅的消息发布者的用户 ID 列表。如果不设置，则取消对整个 Topic 的订阅
    async unsubscribeTopic(topicName: string, options?: {users?: string[];}): Promise<UnsubscribeTopicResponse|ErrorInfo>{
        try {
            // 取消订阅 Topic
            const response: UnsubscribeTopicResponse = {
                timeToken: Date.now(), // （预留属性）操作成功时间戳
            };
            return response;
        } catch (error: any) {
            const errorInfo: ErrorInfo = {
                error: true,
                operation: "unsubscribeTopic",
                errorCode: error.code,
                reason: error.message
            };
            return errorInfo;
        }
    }

    

}
